import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { TimeOffRequest } from '@/client/types/hr';
import { formatRange } from './leave-format';

/** Confirm an approve/deny with an optional note. Shared by HR and My workplace. */
export function LeaveDecisionDialog({
  pending,
  who,
  busy,
  onCancel,
  onConfirm,
}: {
  pending: { request: TimeOffRequest; decision: 'approve' | 'deny' } | null;
  who: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (note: string) => void;
}) {
  const [note, setNote] = useState('');
  const approve = pending?.decision === 'approve';
  const r = pending?.request;
  return (
    <AlertDialog
      open={!!pending}
      onOpenChange={(open) => {
        if (!open) {
          setNote('');
          onCancel();
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{approve ? 'Approve time off?' : 'Deny time off?'}</AlertDialogTitle>
          <AlertDialogDescription>
            {r ? `${who}: ${r.typeName}, ${formatRange(r)} (${r.days} working day${r.days === 1 ? '' : 's'}).` : ''}
            {approve ? ' The days come off their balance.' : ' They will see your note.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <label className="block space-y-1.5">
          <span className="text-xs font-medium text-gray-600">Note {approve ? '(optional)' : '(recommended)'}</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm outline-none ring-blue-500 focus:ring-2"
          />
        </label>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            className={approve ? undefined : 'bg-red-600 hover:bg-red-700'}
            onClick={(e) => {
              e.preventDefault();
              onConfirm(note.trim());
              setNote('');
            }}
          >
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            {approve ? 'Approve' : 'Deny'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
